import { inject, Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  AccountTransaction,
  CustomerBalance,
  DebtorSummary,
  RecordAdvancePaymentDto,
} from '@app/core/models/customer-account.interface';
import { ApiResponse } from '@app/core/adaptors/dto';
import { parametersFromPath } from '@app/core/interceptors/mock-api.interceptor';
import { CustomerMocksService } from '@app/core/services/mocks/customer-mocks.service';

@Injectable({ providedIn: 'root' })
export class CustomerAccountMocksService {
  private customerMocksService = inject(CustomerMocksService);

  private readonly mockDebtors: DebtorSummary[] = [
    {
      customerId: 1,
      tenantId: 1,
      name: 'Tanaka Auto Services',
      contactName: 'Kenji Tanaka',
      mobilePhone: '+64 021 123 4567',
      email: 'kenji@tanakaauto.co.nz',
      availableCredit: 0,
      pendingInvoices: 250.0,
      netAmountDue: 250.0,
    },
    {
      customerId: 2,
      tenantId: 1,
      name: 'Wellington Wheels Ltd',
      contactName: 'Sarah Mitchell',
      mobilePhone: '+64 027 987 6543',
      email: 'sarah@wellingtonwheels.co.nz',
      availableCredit: 0,
      pendingInvoices: 145.5,
      netAmountDue: 145.5,
    },
    {
      customerId: 3,
      tenantId: 1,
      name: 'Christchurch Car Care',
      contactName: "Liam O'Brien",
      mobilePhone: '+64 022 456 7890',
      email: 'liam@chchcarcare.co.nz',
      availableCredit: 0,
      pendingInvoices: 75.0,
      netAmountDue: 75.0,
    },
  ];

  private readonly mockTransactions: AccountTransaction[] = [
    {
      id: 1,
      tenantId: 1,
      customerId: 1,
      transactionType: 'CREDIT',
      amount: 500.0,
      referenceId: null,
      referenceType: null,
      note: 'Advance payment received',
      transactionDate: '2026-02-10',
      createdAt: '2026-02-10T09:00:00Z',
      runningBalance: 500.0,
    },
    {
      id: 2,
      tenantId: 1,
      customerId: 1,
      transactionType: 'DEBIT',
      amount: 320.0,
      referenceId: 101,
      referenceType: 'INVOICE',
      note: 'Invoice #INV-101 applied',
      transactionDate: '2026-02-15',
      createdAt: '2026-02-15T14:30:00Z',
      runningBalance: 180.0,
    },
    {
      id: 3,
      tenantId: 1,
      customerId: 1,
      transactionType: 'DEBIT',
      amount: 80.0,
      referenceId: 102,
      referenceType: 'INVOICE',
      note: 'Invoice #INV-102 applied',
      transactionDate: '2026-02-20',
      createdAt: '2026-02-20T11:00:00Z',
      runningBalance: 100.0,
    },
    {
      id: 4,
      tenantId: 1,
      customerId: 1,
      transactionType: 'CREDIT',
      amount: 150.0,
      referenceId: null,
      referenceType: null,
      note: 'Top-up payment',
      transactionDate: '2026-02-25',
      createdAt: '2026-02-25T10:00:00Z',
      runningBalance: 250.0,
    },
  ];

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const { resourceId } = parametersFromPath(req, 'customers');

    // GET /customers/debtors
    if (req.method === 'GET' && req.url.includes('/customers/debtors')) {
      return this.getDebtors();
    }

    // GET /customers/:id/account/balance
    if (req.method === 'GET' && req.url.includes('/account/balance')) {
      return this.getBalance(resourceId);
    }

    // GET /customers/:id/account/transactions
    if (req.method === 'GET' && req.url.includes('/account/transactions')) {
      return this.getTransactions(resourceId);
    }

    // POST /customers/:id/account/payments
    if (req.method === 'POST' && req.url.includes('/account/payments')) {
      const dto = req.body as RecordAdvancePaymentDto;
      return this.recordAdvancePayment(resourceId, dto);
    }

    throw new Error(`Unsupported mock request: ${req.method} ${req.url}`);
  }

  private getDebtors(): Observable<HttpEvent<any>> {
    const res: ApiResponse<DebtorSummary> = {
      data: this.mockDebtors,
      total: this.mockDebtors.length,
      page: 1,
      limit: 10,
      totalPages: 1,
    };
    return this.mapToResponse(res, 200);
  }

  private getBalance(customerId: number | undefined): Observable<HttpEvent<any>> {
    const debtor = this.mockDebtors.find((d) => d.customerId === customerId);
    const res: CustomerBalance = {
      customerId: customerId ?? 1,
      balance: debtor?.availableCredit ?? 0,
    };
    return this.mapToResponse(res, 200);
  }

  private getTransactions(customerId: number | undefined): Observable<HttpEvent<any>> {
    const transactions = this.mockTransactions.filter(
      (t) => !customerId || t.customerId === customerId
    );
    const res: ApiResponse<AccountTransaction> = {
      data: transactions,
      total: transactions.length,
      page: 1,
      limit: 20,
      totalPages: 1,
    };
    return this.mapToResponse(res, 200);
  }

  private recordAdvancePayment(
    customerId: number | undefined,
    dto: RecordAdvancePaymentDto
  ): Observable<HttpEvent<any>> {
    const newTransaction: AccountTransaction = {
      id: Date.now(),
      tenantId: 1,
      customerId: customerId ?? 1,
      transactionType: 'CREDIT',
      amount: dto.amount,
      referenceId: null,
      referenceType: null,
      note: dto.note ?? null,
      transactionDate: dto.transactionDate ?? new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
      runningBalance: dto.amount,
    };

    // Update the mock debtor credit for this session
    const debtor = this.mockDebtors.find((d) => d.customerId === customerId);
    if (debtor) {
      debtor.availableCredit += dto.amount;
      debtor.netAmountDue = Math.max(0, debtor.pendingInvoices - debtor.availableCredit);
    } else {
      // Customer not yet in the debtors list — look up their details and add them
      const customer = this.customerMocksService
        .getMockCustomers()
        .find((c) => c.id === customerId);
      this.mockDebtors.push({
        customerId: customerId ?? 0,
        tenantId: 1,
        name: customer?.name ?? `Customer #${customerId}`,
        contactName: customer?.contactName ?? '',
        mobilePhone: customer?.mobilePhone ?? null,
        email: customer?.email ?? null,
        availableCredit: dto.amount,
        pendingInvoices: 0,
        netAmountDue: 0,
      });
    }

    return this.mapToResponse(newTransaction, 201);
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(delay(400));
  }
}
